import React, { useState, useEffect, useRef } from 'react';
import { 
  Button, 
  Upload, 
  Progress, 
  message, 
  Card, 
  Form, 
  Space, 
  Typography,
  Table,
  Tag
} from 'antd';
import { 
  UploadOutlined, 
  InboxOutlined, 
  FileZipOutlined,
  FolderOpenOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import AxiosOICStat from "AxiosOICStat";

const { Dragger } = Upload;
const { Title, Text } = Typography;

export const SdgCsvUpload = () => {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [zipFile, setZipFile] = useState(null);
  const [form] = Form.useForm();
  const [folderContents, setFolderContents] = useState([]);
  const [folderLoading, setFolderLoading] = useState(false);
  const [folderInfo, setFolderInfo] = useState(null);
  const uploadController = useRef(null);

  const handleUpload = async () => {
    if (!zipFile) {
      message.error('Please select a ZIP file to upload');
      return;
    }

    try {
      setLoading(true);
      setProgress(0);

      const formData = new FormData();
      formData.append('file', zipFile);

      // Use AbortController to allow cancellation
      uploadController.current = new AbortController();

      const response = await AxiosOICStat.post('/external_api/sdg/upload-zip', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 300000, // 5 minutes timeout
        onUploadProgress: (progressEvent) => {
          try {
            if (progressEvent.lengthComputable) {
              const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setProgress(Math.min(100, percent));
            }
          } catch (e) {
            // ignore progress calc errors
          }
        },
        signal: uploadController.current.signal,
      });

      setProgress(100);
      message.success('ZIP file uploaded successfully!');

      // Reset form after successful upload
      setTimeout(() => {
        setZipFile(null);
        form.resetFields();
        setProgress(0);
      }, 1200);

    } catch (error) {
      console.error('Upload error:', error);
      
      let errorMessage = 'Failed to upload ZIP file';
      if (error.response?.status === 413) {
        errorMessage = 'File size too large - please use a smaller ZIP file';
      } else if (error.response?.status === 408 || error.code === 'ECONNABORTED') {
        errorMessage = 'Upload timeout - file might be too large or connection too slow';
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid ZIP file or content';
      } else if (error.response?.data?.detail) {
        errorMessage = `Error: ${error.response.data.detail}`;
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      message.error(errorMessage, 10);
      
    } finally {
      setLoading(false);
      uploadController.current = null;
      setTimeout(() => setProgress(0), 1500);
    }
  };

  const cancelUpload = () => {
    if (uploadController.current) {
      try {
        uploadController.current.abort();
      } catch (e) {
        // ignore
      }
      setLoading(false);
      setProgress(0);
      message.info('Upload canceled');
      uploadController.current = null;
    }
  };

  const uploadProps = {
    beforeUpload: (file) => {
      const isValidType = file.type === 'application/zip' || 
                         file.type === 'application/x-zip-compressed' ||
                         file.name.toLowerCase().endsWith('.zip');
      
      if (!isValidType) {
        message.error(`${file.name}: Please upload only ZIP files (.zip)`);
        return false;
      }

      const isLt300M = file.size / 1024 / 1024 < 300;
      if (!isLt300M) {
        message.error(`${file.name}: File must be smaller than 300MB`);
        return false;
      }

      // Warning for large files
      const fileSizeMB = file.size / 1024 / 1024;
      if (fileSizeMB > 50) {
        message.warning(`Large ZIP file detected (${fileSizeMB.toFixed(1)}MB). Upload may take longer.`, 5);
      }

      setZipFile(file);
      return false; // Prevent automatic upload
    },
    onRemove: () => {
      setZipFile(null);
    },
    fileList: zipFile ? [zipFile] : [],
    showUploadList: false,
    multiple: false,
    accept: '.zip',
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const fetchFolderContents = async () => {
    try {
      setFolderLoading(true);
      const response = await AxiosOICStat.get('/external_api/sdg/sdg-data-folder-content');
      setFolderContents(response.data.contents || []);
      setFolderInfo({
        folderPath: response.data.folder_path,
        totalItems: response.data.total_items,
        modifiedDate: response.data.modified_date,
        modifiedTime: response.data.modified_time
      });
    } catch (error) {
      console.error('Error fetching folder contents:', error);
      message.error('Failed to load SDG data folder contents');
    } finally {
      setFolderLoading(false);
    }
  };

  useEffect(() => {
    fetchFolderContents();
  }, []);

  const folderColumns = [
    {
      title: 'File Name',
      dataIndex: 'name',
      key: 'name',
      render: (name) => (
        <Space>
          <FileZipOutlined style={{ color: '#1890ff' }} />
          <Text strong>{name}</Text>
        </Space>
      ),
    },
    {
      title: 'Size',
      dataIndex: 'size',
      key: 'size',
      render: (size) => formatFileSize(size),
      sorter: (a, b) => a.size - b.size,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => (
        <Tag color={type === 'file' ? 'blue' : 'green'}>
          {type.toUpperCase()}
        </Tag>
      ),
    },
  ];

  return (
    <div style={{ padding: '14px 12px', width: '100%', margin: '0 auto', fontSize: '13px' }}>
      <Card bordered={false} bodyStyle={{ padding: 12, boxShadow: '0 1px 2px rgba(16,24,40,0.03)', borderRadius: 8, background: '#ffffff' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <div style={{ width: 320 }}>
              <Title level={5} style={{ margin: 0 }}>Upload SDG (ZIP)</Title>
              <div style={{ marginTop: 8 }}>
                <Form form={form} layout="inline" onFinish={handleUpload}>
                  <Form.Item style={{ margin: 0 }}>
                    <Dragger {...uploadProps} style={{ padding: '10px', width: '320px', height: 86, borderRadius: 8, background: '#fbfbfc', border: '1px dashed #e6e6e6' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <InboxOutlined style={{ fontSize: 18, color: '#5b8def' }} />
                        <div>
                          <div style={{ margin: 0, fontSize: 13, color: '#111827' }}>Click or drop a ZIP</div>
                          <div style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>ZIP of Excel files · max 300MB</div>
                        </div>
                      </div>
                    </Dragger>
                  </Form.Item>
                </Form>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'center' }}>
              <div>
                {zipFile ? (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <FileZipOutlined style={{ color: '#6b21a8', fontSize: 14 }} />
                    <Text strong style={{ fontSize: 13, maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{zipFile.name}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>({formatFileSize(zipFile.size)})</Text>
                  </div>
                ) : (
                  <Text type="secondary" style={{ fontSize: 12 }}>No file selected</Text>
                )}
              </div>

              <div>
                <Space size={8}>
                  <Button type="primary" size="small" icon={<UploadOutlined />} onClick={handleUpload} loading={loading} disabled={!zipFile || loading}>Upload</Button>
                  {loading ? (
                    <Button danger size="small" icon={<CloseCircleOutlined />} onClick={cancelUpload}>Cancel</Button>
                  ) : (
                    zipFile && <Button size="small" onClick={() => setZipFile(null)}>Clear</Button>
                  )}
                </Space>
              </div>

              {loading && (
                <div style={{ width: 320 }}>
                  <Progress percent={progress} size="small" strokeColor={{ from: '#60a5fa', to: '#10b981' }} showInfo={false} />
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>{progress < 100 ? 'Uploading…' : 'Done'}</div>
                </div>
              )}
            </div>
          </div>

          <div style={{ marginTop: 8 }}>
            <div style={{ width: 320 }}>
              <div style={{ marginTop: 8, background: '#fbfbfc', padding: 8, borderRadius: 6 }}>
                <Title level={5} style={{ margin: '0 0 8px 0' }}>Instructions</Title>
                <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: '#6b7280' }}>
                  <li>Use the official SDG ZIP</li>
                  <li>Each Excel corresponds to a goal</li>
                  <li>Large uploads can take minutes</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </Card>

      <Card bordered={false} bodyStyle={{ padding: 12, marginTop: 16, borderRadius: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FolderOpenOutlined style={{ fontSize: 16, color: '#374151' }} />
            <Text strong>SDG Data</Text>
          </div>
          <Button icon={<ReloadOutlined />} onClick={fetchFolderContents} size="small" loading={folderLoading}>Refresh</Button>
        </div>

        {folderInfo && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#e6f7ff', padding: '8px 12px', borderRadius: 6, marginBottom: 12 }}>
            <InfoCircleOutlined style={{ color: '#1890ff', fontSize: 14 }} />
            <div style={{ lineHeight: 1.5 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#0b3d91' }}>Total items: {folderInfo.totalItems}</div>
              {folderInfo.modifiedDate && folderInfo.modifiedTime && (
                <div style={{ fontSize: 12, color: '#6b7280' }}>{folderInfo.modifiedDate} {folderInfo.modifiedTime}</div>
              )}
            </div>
          </div>
        )}

        <Table columns={folderColumns} dataSource={folderContents} loading={folderLoading} rowKey="path" pagination={{ pageSize: 5 }} size="small" />

        {folderContents.length === 0 && !folderLoading && (
          <div style={{ textAlign: 'center', padding: 28, color: '#9ca3af' }}>
            <FolderOpenOutlined style={{ fontSize: 28, marginBottom: 8 }} />
            <div style={{ fontSize: 13 }}>No files found</div>
          </div>
        )}
      </Card>
    </div>
  );
};